import {
    FeatureKey,
    OperatingRule,
    PlanDefinition,
} from 'app/core/entitlements/entitlements.types';

/**
 * Plan catalogue.
 *
 * TEMPORARY SOURCE OF TRUTH. The backend will serve this same shape from
 * `GET /users/me`; when it does, EntitlementsService switches source and
 * nothing that consumes it has to change. Keep the shape identical to
 * `Entitlements` so that swap stays a one-liner.
 *
 * Plans are keyed by operating rule because Part 91 and Part 135 are different
 * products built from the same parts, not two rungs of one ladder.
 */

/** Everything a paying Part 91 operator gets; the ladder adds to this. */
const PART91_ESSENTIAL: FeatureKey[] = [
    'billingRates',
    'fuelPrices',
    'multiLeg',
    'crewAssignment',
    'fbo',
    'shareItinerary',
    'calendarSync',
    'documents',
    'expirationAlerts',
    'conflictDetection',
    'flightHours',
    'expenses',
    'csvExport',
    'passengerRequests',
    'aiTelegram',
];

const PART135_OPERATOR: FeatureKey[] = [
    ...PART91_ESSENTIAL,
    'foreFlight',
    'groundHandling',
    'internationalOps',
    'customReports',
    'multipleAdmins',
    'quotes',
    'customers',
    'dutyTracking',
];

const on = (keys: FeatureKey[]): Partial<Record<FeatureKey, boolean>> =>
    keys.reduce((acc, k) => ({ ...acc, [k]: true }), {});

export const PLANS: Record<OperatingRule, PlanDefinition[]> = {
    part91: [
        {
            planCode: 'p91_free',
            planName: 'Free',
            operatingRule: 'part91',
            priceCents: 0,
            upgradeTo: 'p91_essential',
            // Enough of the product to plan and fly one aircraft; the
            // monthly ceiling is what runs out, not the feature list.
            // Reports, passenger requests and the assistant are here to be
            // tried, each on a monthly allowance. A feature nobody has used is
            // a feature nobody will pay for.
            features: on([
                'fbo',
                'multiLeg',
                'crewAssignment',
                'expenses',
                'shareItinerary',
                'csvExport',
                'passengerRequests',
                'aiTelegram',
            ]),
            limits: {
                aircraft: 1,
                pilots: 1,
                crew: 2,
                passengers: 15,
                // Seven of each, and whichever runs out first is the one that
                // stops you: a week flown as a single nine-leg trip used to
                // cost one trip.
                tripsPerMonth: 7,
                legsPerMonth: 7,
                expensesPerMonth: 10,
                reportsPerMonth: 3,
                requestsPerMonth: 5,
                aiMessagesPerMonth: 30,
                storageMb: 1024,
            },
        },
        {
            planCode: 'p91_essential',
            planName: 'Essential',
            operatingRule: 'part91',
            priceCents: 3900,
            upgradeTo: 'p91_flightdept',
            features: on(PART91_ESSENTIAL),
            limits: {
                aircraft: 1,
                pilots: 3,
                crew: 5,
                passengers: 100,
                tripsPerMonth: null,
                legsPerMonth: null,
                expensesPerMonth: null,
                reportsPerMonth: null,
                requestsPerMonth: null,
                aiMessagesPerMonth: null,
                storageMb: 10240,
            },
        },
        {
            planCode: 'p91_flightdept',
            planName: 'Flight Department',
            operatingRule: 'part91',
            priceCents: 7900,
            upgradeTo: null,
            features: on([
                ...PART91_ESSENTIAL,
                'groundHandling',
                'dutyTracking',
                'foreFlight',
                'internationalOps',
                'customReports',
                'multipleAdmins',
                'aiWhatsApp',
            ]),
            limits: {
                aircraft: 3,
                pilots: 10,
                crew: null,
                passengers: null,
                tripsPerMonth: null,
                legsPerMonth: null,
                expensesPerMonth: null,
                reportsPerMonth: null,
                requestsPerMonth: null,
                aiMessagesPerMonth: null,
                storageMb: 51200,
            },
        },
    ],
    part135: [
        {
            planCode: 'p135_trial',
            planName: 'Trial',
            operatingRule: 'part135',
            priceCents: 0,
            upgradeTo: 'p135_operator',
            features: on(['fbo', 'multiLeg', 'crewAssignment', 'shareItinerary', 'csvExport', 'passengerRequests', 'aiTelegram']),
            limits: {
                aircraft: 1,
                pilots: 2,
                crew: 2,
                passengers: 25,
                tripsPerMonth: 10,
                legsPerMonth: 10,
                expensesPerMonth: 10,
                reportsPerMonth: 3,
                requestsPerMonth: 5,
                aiMessagesPerMonth: 30,
                storageMb: 1024,
            },
        },
        {
            planCode: 'p135_operator',
            planName: 'Operator',
            operatingRule: 'part135',
            priceCents: 14900,
            upgradeTo: 'p135_enterprise',
            features: on(PART135_OPERATOR),
            limits: {
                aircraft: 3,
                pilots: 8,
                crew: null,
                passengers: null,
                tripsPerMonth: null,
                legsPerMonth: null,
                expensesPerMonth: null,
                reportsPerMonth: null,
                requestsPerMonth: null,
                aiMessagesPerMonth: null,
                storageMb: 102400,
            },
        },
        {
            planCode: 'p135_enterprise',
            planName: 'Enterprise',
            operatingRule: 'part135',
            priceCents: 34900,
            upgradeTo: null,
            features: on([...PART135_OPERATOR, 'aiWhatsApp']),
            limits: {
                aircraft: 15,
                pilots: 40,
                crew: null,
                passengers: null,
                tripsPerMonth: null,
                legsPerMonth: null,
                expensesPerMonth: null,
                reportsPerMonth: null,
                requestsPerMonth: null,
                aiMessagesPerMonth: null,
                storageMb: 512000,
            },
        },
    ],
};

export const findPlan = (planCode: string): PlanDefinition | undefined =>
    [...PLANS.part91, ...PLANS.part135].find((p) => p.planCode === planCode);
